import { useState, useEffect } from "react";
import { HeartHandshake, X } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = "donaris_promo_dismissed_until";
const BAR_HEIGHT = "36px";
const ROTATE_MS = 8000;

const isDismissed = () => {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return until > Date.now();
  } catch {
    return false;
  }
};

export default function DonarisPromoBar() {
  const [visible, setVisible] = useState(() => !isDismissed());
  const [campaigns, setCampaigns] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    document.documentElement.style.setProperty("--donaris-h", visible ? BAR_HEIGHT : "0px");
    return () => document.documentElement.style.setProperty("--donaris-h", "0px");
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    axios
      .get(`${API_URL}/api/donaris/campaigns`)
      .then((r) => setCampaigns(r.data?.campaigns || []))
      .catch(() => setCampaigns([]));
  }, [visible]);

  useEffect(() => {
    if (campaigns.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % campaigns.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [campaigns.length]);

  if (!visible) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    } catch { /* ignore */ }
    setVisible(false);
  };

  const campaign = campaigns.length > 0 ? campaigns[idx % campaigns.length] : null;
  const href = campaign ? campaign.link : "https://donaris.app";

  return (
    <div
      className="relative pointer-events-auto h-9 flex items-center justify-center gap-2 sm:gap-3 px-3 text-[13px] font-medium"
      style={{
        background: "linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)",
        color: "#FFF7ED",
      }}
      data-testid="donaris-promo-bar"
    >
      <a
        href={href}
        target="_blank"
        rel="noopener"
        className="flex items-center gap-2 min-w-0 group max-w-[calc(100%-40px)]"
        data-testid="donaris-promo-link"
      >
        <HeartHandshake className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        {campaign ? (
          <span
            key={campaign.link}
            className="truncate animate-in fade-in duration-500"
            data-testid="donaris-campaign-title"
          >
            <span className="hidden sm:inline text-white/80">Campanie activă pe Donaris: </span>
            <span className="font-bold underline underline-offset-2 decoration-white/50 group-hover:decoration-white">
              {campaign.title}
            </span>
          </span>
        ) : (
          <span className="truncate">
            <span className="hidden sm:inline">Susține o cauză pe </span>
            <span className="font-bold underline underline-offset-2 decoration-white/50 group-hover:decoration-white">
              Donaris.app
            </span>
            <span className="hidden md:inline"> — donații, strângeri de fonduri și campanii</span>
          </span>
        )}
        <span className="flex-shrink-0 px-2.5 py-0.5 rounded-full bg-white/20 group-hover:bg-white/30 text-white text-xs font-bold transition-colors">
          Donează
        </span>
      </a>
      <button
        onClick={dismiss}
        aria-label="Închide reclama Donaris"
        className="absolute right-2 w-6 h-6 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
        data-testid="donaris-promo-close"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
